import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import pipelineService from '../services/pipelineService';

// Get all pipelines
export const getPipelines = createAsyncThunk(
  'pipelines/getPipelines',
  async (_, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('pipelinesSlice: Calling pipelineService.getAll()');
      }
      const pipelines = await pipelineService.getAll();
      if (process.env.NODE_ENV === 'development') {
        console.log('pipelinesSlice: Received pipelines from service:', pipelines);
      }
      return pipelines;
    } catch (error) {
      console.error('pipelinesSlice: Error fetching pipelines:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get a single pipeline
export const getPipeline = createAsyncThunk(
  'pipelines/getPipeline',
  async (id, { rejectWithValue }) => {
    try {
      return await pipelineService.getById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create a new pipeline
export const createPipeline = createAsyncThunk(
  'pipelines/createPipeline',
  async (pipelineData, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('pipelinesSlice: Creating pipeline with data:', pipelineData);
      }
      const result = await pipelineService.create(pipelineData);
      if (process.env.NODE_ENV === 'development') {
        console.log('pipelinesSlice: Pipeline created successfully:', result);
      }
      return result;
    } catch (error) {
      console.error('pipelinesSlice: Error creating pipeline:', error);

      // Extract the error message
      let errorMessage = error.message;

      // If there's a response with data
      if (error.response?.data) {
        if (process.env.NODE_ENV === 'development') {
          console.error('pipelinesSlice: Error response data:', error.response.data);
        }

        // Handle different error data formats
        if (typeof error.response.data === 'object') {
          // If it's an object, try to extract the message or error field
          errorMessage = error.response.data.message || error.response.data.error || JSON.stringify(error.response.data);
        } else if (typeof error.response.data === 'string') {
          // If it's a string, use it directly
          errorMessage = error.response.data;
        }
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Update a pipeline
export const updatePipeline = createAsyncThunk(
  'pipelines/updatePipeline',
  async ({ id, pipelineData }, { rejectWithValue }) => {
    try {
      return await pipelineService.update(id, pipelineData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete a pipeline
export const deletePipeline = createAsyncThunk(
  'pipelines/deletePipeline',
  async (id, { rejectWithValue }) => {
    try {
      await pipelineService.remove(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  pipelines: [],
  currentPipeline: null,
  loading: false,
  error: null,
  success: false,
};

const pipelinesSlice = createSlice({
  name: 'pipelines',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentPipeline: (state, action) => {
      state.currentPipeline = action.payload;
    },
    clearCurrentPipeline: (state) => {
      state.currentPipeline = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all pipelines
      .addCase(getPipelines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPipelines.fulfilled, (state, action) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('pipelinesSlice: getPipelines fulfilled with payload:', action.payload);
        }
        state.loading = false;
        state.pipelines = action.payload;
        if (process.env.NODE_ENV === 'development') {
          console.log('pipelinesSlice: Updated pipelines state:', state.pipelines);
        }
      })
      .addCase(getPipelines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get single pipeline
      .addCase(getPipeline.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPipeline.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPipeline = action.payload;
      })
      .addCase(getPipeline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create pipeline
      .addCase(createPipeline.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createPipeline.fulfilled, (state, action) => {
        state.loading = false;
        state.pipelines.push(action.payload);
        state.success = true;
      })
      .addCase(createPipeline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update pipeline
      .addCase(updatePipeline.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updatePipeline.fulfilled, (state, action) => {
        state.loading = false;
        state.pipelines = state.pipelines.map(pipeline =>
          pipeline.id === action.payload.id ? action.payload : pipeline
        );
        state.currentPipeline = action.payload;
        state.success = true;
      })
      .addCase(updatePipeline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete pipeline
      .addCase(deletePipeline.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deletePipeline.fulfilled, (state, action) => {
        state.loading = false;
        state.pipelines = state.pipelines.filter(pipeline => pipeline.id !== action.payload);
        state.success = true;
      })
      .addCase(deletePipeline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  setCurrentPipeline,
  clearCurrentPipeline,
} = pipelinesSlice.actions;

export default pipelinesSlice.reducer;